import { skipToken, useQuery } from '@tanstack/react-query'
import SizingApi from '@/infrastructure/api/sizing'
import { useAppSelector } from '@/redux/hooks'

/** Keyed on the Shopify store id; settingsLoaded avoids a 404 flash while the
 * settings request is still in flight. A failed request counts as loaded, or
 * the pages spin forever. */
export function useSizingStore() {
  const settings = useAppSelector((s) => s.settingsApi.settings.response)
  const loading = useAppSelector((s) => s.settingsApi.settings.loading)
  return {
    storeId: settings.shopify_settings?.store_id,
    settingsLoaded: loading === 'succeeded' || loading === 'failed'
  }
}

/** The one gate for Sizing: the navbar entry and the page agree on it. */
export function useHasSizingData(storeId?: string) {
  return useQuery({
    queryKey: ['sizing-has-data', storeId],
    queryFn: storeId
      ? ({ signal }) => SizingApi.hasSizingData(storeId, signal)
      : skipToken,
    staleTime: Infinity
  })
}

export function useSizingOverview(storeId?: string, cycleDate?: string) {
  return useQuery({
    queryKey: ['sizing-overview', storeId, cycleDate],
    queryFn: storeId
      ? ({ signal }) => SizingApi.getOverview(storeId, cycleDate, signal)
      : skipToken,
    staleTime: Infinity
  })
}

export function useSizingModels(storeId?: string, cycleDate?: string) {
  return useQuery({
    queryKey: ['sizing-models', storeId, cycleDate],
    queryFn: storeId
      ? ({ signal }) => SizingApi.getModels(storeId, cycleDate, signal)
      : skipToken,
    staleTime: Infinity
  })
}

export function useSizingModel(
  storeId?: string,
  modelKey?: string,
  cycleDate?: string
) {
  return useQuery({
    queryKey: ['sizing-model', storeId, modelKey, cycleDate],
    queryFn:
      storeId && modelKey
        ? ({ signal }) =>
            SizingApi.getModel(storeId, modelKey, cycleDate, signal)
        : skipToken,
    staleTime: Infinity
  })
}

export function useSizingCycle(storeId?: string, cycleDate?: string) {
  return useQuery({
    queryKey: ['sizing-cycle', storeId, cycleDate],
    queryFn: storeId
      ? ({ signal }) => SizingApi.getCycle(storeId, cycleDate, signal)
      : skipToken,
    staleTime: Infinity
  })
}
